/**
 * Configuration lue à l'exécution, pour ce qui doit pouvoir changer sans
 * reconstruire le plugin.
 *
 * Vite fige ses variables au build : changer le jeton imposait donc de
 * réinstaller les dépendances, reconstruire et republier — une chaîne d'outils
 * complète pour remplacer une chaîne de caractères. Le jeton est désormais lu
 * dans `/config.json`, un fichier du serveur qui s'édite au `nano`.
 *
 * `/config.json` vit hors de la racine web (`/opt/front-radar/config.json`),
 * pour qu'une republication du bundle ne l'écrase pas.
 *
 * Le repli reste `VITE_API_TOKEN` : sans fichier, le plugin se comporte comme
 * avant. C'est ce qui fait marcher `npm run dev`, où `/config.json` n'existe pas.
 */

interface ConfigServeur {
  /** Jeton partagé, identique à PLUGIN_API_TOKEN côté pipeline. */
  jeton?: string;
}

const JETON_DU_BUILD: string = import.meta.env.VITE_API_TOKEN ?? '';

/**
 * Une seule requête pour toute la vie du panneau : la promesse est mémorisée,
 * pas la valeur, pour que les appels concurrents du démarrage n'en déclenchent
 * pas plusieurs.
 */
let promesse: Promise<string> | null = null;

async function lireConfig(): Promise<string> {
  try {
    // `cache: 'no-store'` : sans cela, un jeton changé sur le serveur resterait
    // invisible tant que le navigateur garderait l'ancienne réponse.
    const reponse = await fetch('/config.json', { cache: 'no-store' });
    if (!reponse.ok) return JETON_DU_BUILD;

    const config = (await reponse.json()) as ConfigServeur;
    const jeton = typeof config.jeton === 'string' ? config.jeton.trim() : '';
    return jeton || JETON_DU_BUILD;
  } catch {
    // Fichier absent, JSON illisible, réseau coupé : on retombe sur le build
    // plutôt que d'empêcher le plugin de démarrer.
    return JETON_DU_BUILD;
  }
}

export function jeton(): Promise<string> {
  if (!promesse) promesse = lireConfig();
  return promesse;
}

/** Force une relecture — utile après une modification du fichier sur le serveur. */
export function oublierConfig(): void {
  promesse = null;
}
